import React from 'react';
import * as Sentry from '@sentry/react';
import {VideoConstraints, WebcamRefTypes} from '../Webcam';
import {IV_PHOTO_SOURCE} from '@guestapp/sdk';
import {useActions, useAppSelector, useErrorModal} from '../../hooks';
import {ANALYTIC_EVENTS, Analytics} from '../../analytics';
import {Buttons} from './components/Buttons';
import {UploadPhoto} from './components/UploadPhoto';
import {TakePhoto, type MobileScreenshotPayLoad} from './components/TakePhoto';
import {Wrapper} from './styled';

enum UPLOAD_PHOTO_STATES {
  take = 'TAKE',
  confirmTaking = 'CONFIRM_TAKING',
  upload = 'UPLOAD',
}

type PhotoBoxProps = {
  mobileHelperText?: React.ReactNode | string;
  mobileHelperTitle?: React.ReactNode | string;
  preloadedScreenshot?: string;
  placeholderImage?: string;
  onUploadPhoto?: (props: {image: string; source: IV_PHOTO_SOURCE}) => void;
  isUpload?: boolean;
  isOneSide?: boolean;
  roundedScanArea?: boolean;
  additionalConstraints?: VideoConstraints;
};
const PhotoBox = ({
  isUpload,
  preloadedScreenshot,
  onUploadPhoto,
  placeholderImage,
  additionalConstraints,
  mobileHelperText,
  mobileHelperTitle,
  roundedScanArea,
}: PhotoBoxProps) => {
  const webcamRef = React.useRef<WebcamRefTypes>(null);
  const [uploadPhotoState, setUploadPhotoState] = React.useState<UPLOAD_PHOTO_STATES>(
    UPLOAD_PHOTO_STATES.take,
  );
  const uploadedImage = useAppSelector(state => state.photoBox.uploadedImage);
  const {setPhotoBoxUploadedImage, resetPhotoBoxUploadedImage} = useActions();
  const [disabled, setDisabled] = React.useState(false);
  const {displayError, ErrorModal} = useErrorModal();

  React.useEffect(() => {
    if (isUpload) {
      setUploadPhotoState(UPLOAD_PHOTO_STATES.upload);
    } else if (uploadedImage && !isUpload) {
      setUploadPhotoState(UPLOAD_PHOTO_STATES.confirmTaking);
    } else {
      setUploadPhotoState(UPLOAD_PHOTO_STATES.take);
    }
  }, [isUpload, uploadedImage]);

  React.useEffect(
    function resetImageWhenLeavePage() {
      return () => {
        resetPhotoBoxUploadedImage();
      };
    },
    [resetPhotoBoxUploadedImage],
  );

  const handleTakePhoto = React.useCallback(
    async (payload: MobileScreenshotPayLoad) => {
      try {
        Analytics.track(ANALYTIC_EVENTS.ivPhotoTaken, {
          'Used camera': payload.isNative ? 'native' : 'app',
        });

        const image = payload.isNative
          ? await payload.photo
          : await webcamRef?.current?.takePhoto();
        if (image)
          onUploadPhoto?.({
            image,
            source: payload.isNative
              ? IV_PHOTO_SOURCE.nativeCamera
              : IV_PHOTO_SOURCE.appCamera,
          });
      } catch (err) {
        Analytics.track(ANALYTIC_EVENTS.ivTakingPhotoError, {
          'Error message': (err as Error)?.message,
        });
        Sentry.captureException(err);
        displayError(err);
      }
    },
    [displayError, onUploadPhoto],
  );

  const handleUploadPhoto = React.useCallback(
    (image: string) => {
      setPhotoBoxUploadedImage(image);
    },
    [setPhotoBoxUploadedImage],
  );

  const handleChangePhoto = React.useCallback(() => {
    resetPhotoBoxUploadedImage();
    Analytics.track(ANALYTIC_EVENTS.ivResetPhoto);
  }, [resetPhotoBoxUploadedImage]);

  const handleFinishUploadPhoto = React.useCallback(() => {
    if (!uploadedImage) return;
    Analytics.track(ANALYTIC_EVENTS.ivPhotoUploaded);
    onUploadPhoto?.({image: uploadedImage, source: IV_PHOTO_SOURCE.external});
  }, [onUploadPhoto, uploadedImage]);

  const getContent = React.useCallback(() => {
    if (isUpload) {
      return (
        <UploadPhoto
          onUpload={handleUploadPhoto}
          onDisabled={setDisabled}
          image={uploadedImage}
        />
      );
    }

    return (
      <TakePhoto
        ref={webcamRef}
        disabled={disabled}
        onDisabled={setDisabled}
        roundedScanArea={roundedScanArea}
        additionalConstraints={additionalConstraints}
        onScreenshot={handleTakePhoto}
        placeholderImage={placeholderImage}
        mobileHelperText={mobileHelperText}
        mobileHelperTitle={mobileHelperTitle}
        mirrored={roundedScanArea}
      />
    );
  }, [
    isUpload,
    roundedScanArea,
    additionalConstraints,
    handleTakePhoto,
    placeholderImage,
    mobileHelperText,
    mobileHelperTitle,
    handleUploadPhoto,
    uploadedImage,
    setDisabled,
    disabled,
  ]);

  return (
    <Wrapper className="photo-box">
      {getContent()}
      <Buttons
        state={uploadPhotoState}
        onCapture={() => handleTakePhoto({isNative: false})}
        onChange={handleChangePhoto}
        disabled={disabled}
        onFinishUploadPhoto={handleFinishUploadPhoto}
      />
      <ErrorModal />
    </Wrapper>
  );
};

export {PhotoBox, UPLOAD_PHOTO_STATES};
